"use client"

import * as React from "react"
import { LucideIcon } from 'lucide-react'
import {
  UICard,
  UICardContent,
  UICardFooter,
  UICardHeader,
  UICardTitle,
  UICardDescription
} from './ui-card'
import { UIButton } from '../buttons/ui-button'
import { UITypography } from '../display/ui-typography'
import { cn } from '../../lib/utils'
import { UIBox } from '../layout/ui-box'

export interface UICardBackgroundProps
  extends Omit<React.ComponentProps<typeof UICard>, 'children'> {
  title: string
  description?: string
  icon?: LucideIcon
  backgroundVariant?: 'subtle' | 'accent' | 'muted'
  primaryAction?: {
    text: string
    onClick: () => void
    variant?: 'default' | 'accent' | 'outline'
  }
  secondaryAction?: {
    text: string
    onClick: () => void
    variant?: 'outline' | 'ghost'
  }
  children?: React.ReactNode
}

/**
 * Background Card Component
 * Card with subtle background for less prominent content
 * Based on shadcn/ui "Card with Background" block
 */
export const UICardBackground = React.forwardRef<HTMLDivElement, UICardBackgroundProps>(
  ({
    title,
    description,
    icon: Icon,
    backgroundVariant = 'subtle',
    primaryAction,
    secondaryAction,
    className,
    children,
    ...props
  }, ref) => {
    return (
      <UICard
        ref={ref}
        className={cn(
          "border-none shadow-none",
          {
            "bg-secondary/30 dark:bg-secondary/20": backgroundVariant === 'subtle',
            "bg-accent/10 dark:bg-accent/5": backgroundVariant === 'accent',
            "bg-muted/50 dark:bg-muted/30": backgroundVariant === 'muted',
          },
          className
        )}
        {...props}
      >
        {/* Header */}
        <UICardHeader>
          <UIBox className="flex items-start gap-3">
            {Icon && (
              <UIBox className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-background/80 dark:bg-background/40">
                <Icon className="h-5 w-5 text-foreground" />
              </UIBox>
            )}

            <UIBox className="min-w-0 flex-1">
              <UICardTitle>
                <UITypography variant="h4" size="default" weight="semibold">
                  {title}
                </UITypography>
              </UICardTitle>

              {description && (
                <UICardDescription className="mt-1">
                  <UITypography variant="p" size="sm" color="muted">
                    {description}
                  </UITypography>
                </UICardDescription>
              )}
            </UIBox>
          </UIBox>
        </UICardHeader>

        {/* Content */}
        {children && (
          <UICardContent className="py-4">
            {children}
          </UICardContent>
        )}

        {/* Footer with actions */}
        {(primaryAction || secondaryAction) && (
          <UICardFooter className="pt-4">
            <UIBox className="flex w-full gap-2">
              {primaryAction && (
                <UIButton
                  variant={primaryAction.variant || 'default'}
                  onClick={primaryAction.onClick}
                  className="flex-1"
                >
                  {primaryAction.text}
                </UIButton>
              )}
              {secondaryAction && (
                <UIButton
                  variant={secondaryAction.variant || 'outline'}
                  onClick={secondaryAction.onClick}
                  className={primaryAction ? "flex-1" : "w-full"}
                >
                  {secondaryAction.text}
                </UIButton>
              )}
            </UIBox>
          </UICardFooter>
        )}
      </UICard>
    )
  }
)

UICardBackground.displayName = "UICardBackground"